import type { Landmark, LatLon } from './types.ts';
import gazetteerData from './gazetteer/puget-sound.json' with { type: 'json' };

export const GAZETTEER: Landmark[] = gazetteerData as Landmark[];

const EARTH_RADIUS_NM = 3440.065;
const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/**
 * Bounding box for the Salish Sea. The Acartia feed is coast-wide (we have seen
 * records as far south as 37.5N in California), so every ingest must filter to
 * this box or unrelated sightings will be chained into Puget Sound tracks.
 */
export const SALISH_SEA_BBOX = {
  minLat: 46.9,
  maxLat: 49.2,
  minLon: -124.9,
  maxLon: -122.0,
} as const;

export function inSalishSea(lat: number, lon: number): boolean {
  return (
    lat >= SALISH_SEA_BBOX.minLat &&
    lat <= SALISH_SEA_BBOX.maxLat &&
    lon >= SALISH_SEA_BBOX.minLon &&
    lon <= SALISH_SEA_BBOX.maxLon
  );
}

/** Great-circle distance in nautical miles. */
export function haversineNm(a: LatLon, b: LatLon): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing from `a` to `b`, in degrees clockwise from true north. */
export function bearingDeg(a: LatLon, b: LatLon): number {
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

export function compassFromBearing(deg: number): (typeof COMPASS)[number] {
  const i = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
  return COMPASS[i]!;
}

export interface NearestResult {
  landmark: Landmark;
  distanceNm: number;
}

/**
 * Reverse-geocode a position to the closest known landmark.
 * Linear scan: the gazetteer is a few hundred entries, so this is far cheaper
 * than maintaining a spatial index.
 */
export function nearestLandmark(
  lat: number,
  lon: number,
  gazetteer: Landmark[] = GAZETTEER,
): NearestResult | null {
  let best: NearestResult | null = null;
  for (const landmark of gazetteer) {
    const distanceNm = haversineNm({ lat, lon }, landmark);
    if (best === null || distanceNm < best.distanceNm) {
      best = { landmark, distanceNm };
    }
  }
  return best;
}

/**
 * Human-readable position label, e.g. "off Point No Point".
 * Proximity bands deliberately mirror how observers talk about the water.
 */
export function placeLabel(
  lat: number,
  lon: number,
  gazetteer: Landmark[] = GAZETTEER,
): { label: string; landmark: Landmark | null; distanceNm: number | null } {
  const nearest = nearestLandmark(lat, lon, gazetteer);
  if (!nearest) return { label: 'Unknown waters', landmark: null, distanceNm: null };

  const { landmark, distanceNm } = nearest;
  let label: string;
  if (distanceNm < 0.5) label = `at ${landmark.name}`;
  else if (distanceNm < 2) label = `off ${landmark.name}`;
  else if (distanceNm < 5) label = `near ${landmark.name}`;
  else label = `${distanceNm.toFixed(1)} nm from ${landmark.name}`;

  return { label, landmark, distanceNm };
}

const ALIAS_INDEX: Array<{ landmark: Landmark; needle: string }> = GAZETTEER.flatMap(
  (landmark) =>
    [landmark.name, ...landmark.aliases].map((needle) => ({
      landmark,
      needle: needle.toLowerCase(),
    })),
).sort((a, b) => b.needle.length - a.needle.length); // longest match first

/**
 * Find landmarks named in free text. Used to cross-check the reverse-geocoded
 * label against what the observer actually wrote — see scripts/audit-gazetteer.ts.
 */
export function matchLandmarks(text: string): Landmark[] {
  if (!text) return [];
  const haystack = text.toLowerCase();
  const found = new Map<string, Landmark>();
  for (const { landmark, needle } of ALIAS_INDEX) {
    if (found.has(landmark.id)) continue;
    // Word-boundary check so "PT" does not match inside "point".
    const idx = haystack.indexOf(needle);
    if (idx === -1) continue;
    const before = idx === 0 ? ' ' : haystack[idx - 1]!;
    const after = haystack[idx + needle.length] ?? ' ';
    if (/[a-z0-9]/.test(before) || /[a-z0-9]/.test(after)) continue;
    found.set(landmark.id, landmark);
  }
  return [...found.values()];
}

/** Speed in knots implied by moving between two timestamped positions. */
export function impliedKnots(
  a: LatLon & { observedAt: number },
  b: LatLon & { observedAt: number },
): number {
  const hours = Math.abs(b.observedAt - a.observedAt) / 3_600_000;
  if (hours === 0) return Number.POSITIVE_INFINITY;
  return haversineNm(a, b) / hours;
}

/**
 * Parse a coordinate pair typed or pasted by a person.
 *
 * Accepts the forms people actually have on hand:
 *   47.6062, -122.3321           decimal degrees (Google Maps "copy coordinates")
 *   47.6062 -122.3321
 *   47.6062N 122.3321W           hemisphere letters, before or after
 *   47 36.372 N, 122 19.926 W    degrees + decimal minutes (marine charts, GPS)
 *   47 36 22 N 122 19 55 W       degrees, minutes, seconds
 *
 * A pair given as "lon, lat" is detected and swapped: no latitude here exceeds
 * 90, while every Salish Sea longitude is beyond 90 in magnitude, so the two
 * orders cannot be confused. Returns null for anything ambiguous or out of range.
 */
export function parseCoordinates(input: string): LatLon | null {
  const text = input
    .replace(/[°′″ʹʺ'"]/g, ' ')
    .replace(/,/g, ' ')
    .trim();
  if (!text) return null;

  const hasLetters = /[nsew]/i.test(text);

  let a: number | null = null;
  let b: number | null = null;

  if (!hasLetters) {
    const nums = text.match(/-?\d+(?:\.\d+)?/g);
    if (!nums || nums.length !== 2) return null;
    a = Number(nums[0]);
    b = Number(nums[1]);
  } else {
    // Hemisphere letters mark the boundary between the two coordinates. With
    // the letter first ("N47 36.372 W122 19.926") split before each letter; with
    // it last ("47 36.372 N 122 19.926 W") split after each. A single greedy
    // regex cannot do this, because the second coordinate's leading letter
    // looks exactly like the first coordinate's trailing one.
    const segments = (/^\s*[nsew]/i.test(text) ? text.split(/(?=[nsew])/i) : text.split(/(?<=[nsew])/i))
      .map((seg) => seg.trim())
      .filter(Boolean);
    if (segments.length !== 2) return null;

    const parsed = segments.map((seg) => {
      const letters = seg.match(/[nsew]/gi);
      if (letters && letters.length > 1) return null;
      const nums = seg.match(/\d+(?:\.\d+)?/g);
      if (!nums || nums.length > 3) return null;
      const deg = Number(nums[0]);
      const min = nums[1] === undefined ? 0 : Number(nums[1]);
      const sec = nums[2] === undefined ? 0 : Number(nums[2]);
      if (min >= 60 || sec >= 60) return null;
      const hemi = letters?.[0]?.toUpperCase() ?? '';
      let value = deg + min / 60 + sec / 3600;
      if (hemi === 'S' || hemi === 'W' || seg.includes('-')) value = -value;
      const axis: 'lat' | 'lon' | null = hemi === 'N' || hemi === 'S' ? 'lat' : hemi === 'E' || hemi === 'W' ? 'lon' : null;
      return { value, axis };
    });
    if (parsed[0] === null || parsed[1] === null || parsed[0] === undefined || parsed[1] === undefined) return null;

    let [first, second] = [parsed[0], parsed[1]];
    // One letter may be omitted ("47.6N -122.3"); it is the other axis.
    if (first.axis === null && second.axis !== null) first = { ...first, axis: second.axis === 'lat' ? 'lon' : 'lat' };
    if (second.axis === null && first.axis !== null) second = { ...second, axis: first.axis === 'lat' ? 'lon' : 'lat' };
    if (first.axis === null || second.axis === null || first.axis === second.axis) return null;

    // The letters say which is which, so honour them over the order given.
    return first.axis === 'lat' ? finish(first.value, second.value) : finish(second.value, first.value);
  }

  if (a === null || b === null) return null;
  // Longitude-first pair: swap when the first cannot possibly be a latitude.
  if (Math.abs(a) > 90 && Math.abs(b) <= 90) [a, b] = [b, a];
  return finish(a, b);
}

function finish(lat: number, lon: number): LatLon | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}
